import { z } from 'zod';

/** Mirrors invite.backend `Role` (src/@types/authentication.types.ts). */
export enum Role {
  USER = 1,
  TEAM_ADMIN = 2,
  ADMIN = 3,
  BOOKING = 4,
  MANAGER = 5,
  GUEST = 6,
  GLOBAL_ADMIN = 7,
  INTEGRATOR = 8,
  APP_USER_GUEST = 9,
  INSTAY_TEAM = 10,
  CLEANING = 11,
  MAINTENANCE = 12,
  OWNER = 13
}

// Claims come from createTokenCredentials(): the user row minus teams/password/2FA, plus
// role, roles, teamId (null for a global admin without a team), type and activationId (app login).
const Claims = z.looseObject({
  id: z.number(),
  teamId: z.number().nullable(),
  role: z.enum(Role).optional(),
  roles: z.array(z.enum(Role)).optional(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  activationId: z.string().optional(),
  exp: z.number().optional()
});

export type Principal = z.infer<typeof Claims>;

const base64UrlDecode = (part: string): string => {
  const normalised = part.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

/** Decodes the JWT payload without verifying it; the backend verifies on every request. */
export const decodePrincipal = (token: string): Principal => {
  const payload = token.split('.')[1];
  if (payload === undefined) {
    throw new Error('Malformed token');
  }
  return Claims.parse(JSON.parse(base64UrlDecode(payload)));
};

export const hasRole = (principal: Principal, role: Role): boolean =>
  principal.role === role || (principal.roles ?? []).includes(role);

/** App (native) logins carry the activation id in the token. */
export const isAppUser = (principal: Principal): boolean => principal.activationId !== undefined;
